import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, input } from '@angular/core';
import { MetadataEntityData, resolveViewGroupHeader, type EntityView } from '@jms/model';
import { GroupHeaderComponent } from '../../group.header';

/**
 * The metadata-driven fallback swimlane header.
 *
 * The words are not this component's: it resolves the lane's header definition and hands it to the
 * shared `board-group-header`, which the table's group row renders too — one Job reads the same
 * whether it heads a swimlane or a group row. What stays here is the kanban's own contract: the
 * lane's inputs, and the row the lane's fields are read off.
 *
 * Values come from the lane's FIRST row rather than the lane key, because the header names the
 * parent the lane groups by (a job number wants its customer and site beside it) and parent fields
 * ride on every unwound row of that parent.
 */
@Component({
  selector: 'kanban-swimlane-header',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [GroupHeaderComponent],
  template: `<board-group-header [header]="$header()" [record]="$row()" [metadata]="$metadata()" [count]="$count()" [countLabel]="$countLabel()" [groupName]="$laneName()" />`,
})
export class KanbanSwimlaneHeaderComponent {

  /** The lane's first row — every parent field the header elevates rides on it. */
  public readonly $row = input<any>(null, { alias: 'row' });
  public readonly $laneName = input<string>('', { alias: 'laneName' });
  public readonly $countLabel = input<string>('', { alias: 'countLabel' });
  public readonly $count = input<number | null>(null, { alias: 'count' });
  public readonly $view = input<EntityView | null>(null, { alias: 'view' });
  public readonly $metadata = input<MetadataEntityData | null>(null, { alias: 'metadata' });
  public readonly $rowField = input<string | null>(null, { alias: 'rowField' });

  protected readonly $header = computed(() => resolveViewGroupHeader(this.$view(), this.$metadata(), this.$rowField()));
}
